import {
  createProject,
  updateProject,
  deleteProject,
  createProjectSettings,
  createProjectMember
} from '../graphql/mutations.js';
import { getProject, projectMembersByUser } from '../graphql/queries.js';
import { toAwsDateTime, sanitizeInput } from '../utils/dateCoerce';
import { projectMembersByProjectLite, projectMembersByProjectWithUser, getProjectSettingsBundle, getProjectLite, updateProjectSettingsBundle, updateClassifiedDocumentTags } from '../graphql/customQueries.js';
import {
  extractEntityPayloadForMigration,
  needsEntityPayloadMigration,
  stripEntityPayloadFromDescription,
} from '../utils/projectEntityHelpers';
import { deleteProjectMember } from '../graphql/mutations.js';

const APPSYNC_URL = process.env.REACT_APP_APPSYNC_API_URL;
const API_BASE_CLASSIFIER = process.env.REACT_APP_API_BASE_CLASSIFIER || 'http://localhost:8000';

async function graphqlRequest({ query, variables }) {
  const idToken = localStorage.getItem('id_token');

  if (!idToken) {
    console.warn('[graphqlRequest] No id_token in localStorage');
    const error = new Error('User is not authenticated');
    error.name = 'NoValidAuthTokens';
    throw error;
  }

  const body = {
    query,
    variables: variables || {},
  };

  const res = await fetch(APPSYNC_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    console.error('[graphqlRequest] HTTP error', res.status, text);
    throw new Error(`GraphQL HTTP error ${res.status}`);
  }

  const data = await res.json();

  if (data.errors) {
    console.error('[graphqlRequest] GraphQL errors:', data.errors);
    const messages = data.errors.map((e) => e.message).join('\n');
    const err = new Error(messages);
    err.errors = data.errors;
    throw err;
  }

  return data;
}

class ProjectService {
  async getProjectMembers(projectId) {
    if (!projectId) return [];

    let nextToken = null;
    const members = [];

    do {
      const res = await graphqlRequest({
        query: projectMembersByProjectWithUser,
        variables: { projectId, limit: 200, nextToken },
      });

      const conn = res?.data?.projectMembersByProject;
      members.push(...(conn?.items || []));
      nextToken = conn?.nextToken || null;
    } while (nextToken);

    return members;
  }
  async getProjects(authState) {
    try {
      if (!authState?.isAuthenticated || !authState?.user) return [];

      const userId = authState.user.profile?.sub || authState.user.sub;
      if (!userId) return [];

      // 1) Fetch ALL memberships for this user (handles pagination)
      const memberships = [];
      let nextToken = null;

      do {
        const membersRes = await graphqlRequest({
          query: projectMembersByUser,
          variables: {
            userId,
            limit: 200,
            nextToken,
          },
        });

        const conn = membersRes?.data?.projectMembersByUser;
        const items = conn?.items || [];
        memberships.push(...items);

        nextToken = conn?.nextToken || null;
      } while (nextToken);

      // 2) Extract projectIds 
      const projectIds = Array.from(
        new Set(
          memberships
            .map((m) => m?.projectId)
            .filter(Boolean)
        )
      );

      if (projectIds.length === 0) return [];

      // 3) Fetch each project by id 
      const projects = await Promise.all(
        projectIds.map(async (projectId) => {
          try {
            const projectRes = await graphqlRequest({
              query: getProjectLite,
              variables: { id: projectId },
            });
            return projectRes?.data?.getProject || null;
          } catch (e) {
            console.error(`[getProjects] Failed getProject(${projectId}):`, e);
            return null;
          }
        })
      );

      // 4) Filter nulls + optionally sort
      const cleaned = projects.filter(Boolean);

      cleaned.sort((a, b) => {
        const ad = new Date(a.lastModified || a.updatedAt || 0).getTime();
        const bd = new Date(b.lastModified || b.updatedAt || 0).getTime();
        return bd - ad;
      });

      return cleaned;
    } catch (error) {
      console.error('Error fetching projects:', error);

      if (error.name === 'UserUnAuthenticatedException' || error.name === 'NoValidAuthTokens') {
        return [];
      }

      if (error.errors) {
        const messages = error.errors.map((e) => e.message).join('\n');
        throw new Error('Failed to fetch projects: ' + messages);
      }

      throw new Error('Failed to fetch projects: ' + (error.message || 'Unknown error'));
    }
  }

  async getProject(projectId) {
    try {
      const result = await graphqlRequest({
        query: getProjectSettingsBundle,
        variables: { id: projectId },
      });

      let project = result.data.getProject;
      if (needsEntityPayloadMigration(project)) {
        project = await this.migrateEntityPayloadIfNeeded(project);
      }
      return project;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw new Error('Failed to fetch project: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Move projectCategory / entitiesData / doc tags out of description into native fields.
   * Safe to call repeatedly — no-op when migration is not needed.
   */
  async migrateEntityPayloadIfNeeded(project) {
    if (!project?.id || !needsEntityPayloadMigration(project)) return project;

    const extracted = extractEntityPayloadForMigration(project);
    if (!extracted) return project;

    if (process.env.NODE_ENV === 'development') {
      console.log('[migrateEntityPayload] migrating project', project.id);
    }

    await this.updateProject(project.id, {
      description: extracted.cleanDescription,
      projectCategory: extracted.projectCategory,
      entitiesData: extracted.entitiesData,
    });

    const tagEntries = Object.entries(extracted.documentTags || {});
    for (const [docId, ids] of tagEntries) {
      if (!docId || !Array.isArray(ids) || !ids.length) continue;
      try {
        await graphqlRequest({
          query: updateClassifiedDocumentTags,
          variables: { input: { id: docId, projectBuildingDataIds: ids } },
        });
      } catch (e) {
        console.warn('[migrateEntityPayload] doc tag migration skipped for doc', docId);
      }
    }

    const refreshed = await graphqlRequest({
      query: getProjectSettingsBundle,
      variables: { id: project.id },
    });
    return refreshed.data.getProject;
  }

  async getProjectsByName(authState, name) {
    if (!authState?.isAuthenticated || !authState?.user) return [];

    const userId = authState.user.profile?.sub || authState.user.sub;
    if (!userId) return [];

    // get visible projects first
    const projects = await this.getProjects(authState);

    // filter by name client-side 
    const needle = (name || '').trim().toLowerCase();
    if (!needle) return projects;

    return projects.filter(p => (p?.name || '').toLowerCase() === needle);
  }


  async createProject(projectData, authState) {
  try {
    if (!authState?.isAuthenticated || !authState?.user) {
      throw new Error('User not authenticated');
    }

    const userId = authState.user.profile?.sub || authState.user.sub;
    if (!userId) {
      throw new Error('No user ID found in auth state');
    }

    if (!projectData?.name) {
      throw new Error('Project name is required');
    }

    const projectCategory = projectData.projectCategory || null;
    const entitiesData = projectData.entitiesData || null;

    const input = {
      name: projectData.name,
      type: projectData.type || projectData.projectType || 'Unspecified',
      description: projectData.description || '',
      location: projectData.address || '',
      startDate: projectData.startDate || new Date().toISOString(),
      estimatedCompletion: projectData.estimatedCompletion || null,
      lastModified: new Date().toISOString(),
      buildingFootprintArea: parseFloat(projectData.buildingFootprintArea) || 0,
      buildingFootprintAreaExisting: projectData.buildingFootprintAreaExisting != null ? parseFloat(projectData.buildingFootprintAreaExisting) : null,
      buildingFootprintAreaProposed: projectData.buildingFootprintAreaProposed != null ? parseFloat(projectData.buildingFootprintAreaProposed) : null,
      grossFloorAreaExisting: projectData.grossFloorAreaExisting != null ? parseFloat(projectData.grossFloorAreaExisting) : null,
      grossFloorAreaProposed: projectData.grossFloorAreaProposed != null ? parseFloat(projectData.grossFloorAreaProposed) : null,
      roughConstructionBudget: parseFloat(projectData.roughConstructionBudget) || 0,
      siteArea: parseFloat(projectData.siteArea) || 0,
      phase: projectData.phase || '',
      grossFloorArea: parseFloat(projectData.grossFloorArea) || 0,
      storeysAboveGrade: parseInt(projectData.storeysAboveGrade) || 0,
      storeysBelowGrade: parseInt(projectData.storeysBelowGrade) || 0,
      averageGrade: parseFloat(projectData.averageGrade) || 0,
      adjacentStreets: parseInt(projectData.adjacentStreets) || 0,
      zoningDesignation: projectData.zoningDesignation ?? null,
      buildingClassification: projectData.buildingClassification || '',
      occupancyRegion: projectData.occupancyRegion || null,
      occupancyTypes: projectData.occupancyTypes || [],
      occupancyCodes: projectData.occupancyCodes || [],
      majorOccupancy: projectData.majorOccupancy || null,
      constructionType: projectData.constructionType || null,
      status: projectData.status || 'active',
      owner: userId,
      companyId: projectData.companyId,
      buildingHeight: parseFloat(projectData.buildingHeight) || null,
      highBuilding: projectData.highBuilding || false,
      sprinklerSystem: projectData.sprinklerSystem || null,
      fireAlarm: projectData.fireAlarm || null,
      standpipe: projectData.standpipe || null,
      hazardousSubstances: projectData.hazardousSubstances || false,
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[createProject] input:', {
        name: input.name,
        type: input.type,
        companyId: input.companyId,
        owner: `${String(input.owner).slice(0, 8)}...`,
      });
    }

    // 1) Create project
    const result = await graphqlRequest({
      query: createProject,
      variables: { input },
    });

    const created = result?.data?.createProject;
    if (!created?.id) {
      throw new Error('Project creation failed: No data returned from server');
    }

    const projectId = created.id;

    if (projectCategory || entitiesData) {
      const bundleInput = sanitizeInput({
        id: projectId,
        projectCategory,
        entitiesData,
        buildingClassification: input.buildingClassification || undefined,
        constructionType: input.constructionType || undefined,
        description: stripEntityPayloadFromDescription(projectData.description || ''),
      });

      try {
        await graphqlRequest({
          query: updateProjectSettingsBundle,
          variables: { input: bundleInput },
        });
      } catch (bundleError) {
        console.error('[createProject] updateProjectSettingsBundle failed:', bundleError);
        throw new Error(
          'Project was created but multi-building settings could not be saved. Open Project Settings to retry.'
        );
      }
    }

    // 2) Create ProjectMember
    const memberInput = {
      projectId,
      userId,
      role: 'OWNER',
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[createProject] creating OWNER membership:', {
        projectId,
        userId: `${String(userId).slice(0, 8)}...`,
        role: 'OWNER',
      });
    }

    await graphqlRequest({
      query: createProjectMember,
      variables: { input: memberInput },
    });

    // 3) Create project settings 
    const settingsInput = {
      theme: 'light',
      notifications: true,
      autoSave: true,
      owner: userId,
      projectId,
    };

    try {
      await graphqlRequest({
        query: createProjectSettings,
        variables: { input: settingsInput },
      });
    } catch (settingsError) {
      console.error('[createProject] createProjectSettings failed (continuing):', settingsError);
    }

    // 4) Fetch latest project 
    const updatedResult = await graphqlRequest({
      query: getProject,
      variables: { id: projectId },
    });

    const projectWithSettings = updatedResult?.data?.getProject || created;

    return projectWithSettings;
  } catch (error) {
    console.error('Error creating project:', error);

    if (error?.errors) {
      const messages = error.errors.map((e) => e.message).join('\n');
      throw new Error(`Project creation failed: ${messages}`);
    }

    throw new Error('Project creation failed: ' + (error.message || 'Unknown error'));
  }
}


  async updateProject(projectId, projectData) {
    const projectCategory = projectData?.projectCategory;
    const entitiesData = projectData?.entitiesData;
    const description = stripEntityPayloadFromDescription(projectData?.description || '');

    const inputRaw = {
      id: projectId,

      name: projectData?.name,
      description,
      location: projectData?.location,
      owner: projectData?.owner,
      type: projectData?.type,
      phase: projectData?.phase,
      teamId: projectData?.teamId,
      zoningDesignation: projectData?.zoningDesignation,
      buildingClassification: projectData?.buildingClassification,

      adjacentStreets: projectData?.adjacentStreets,
      averageGrade: projectData?.averageGrade,
      buildingFootprintArea: projectData?.buildingFootprintArea,
      buildingFootprintAreaExisting: projectData?.buildingFootprintAreaExisting,
      buildingFootprintAreaProposed: projectData?.buildingFootprintAreaProposed,
      grossFloorAreaExisting: projectData?.grossFloorAreaExisting,
      grossFloorAreaProposed: projectData?.grossFloorAreaProposed,
      grossFloorArea: projectData?.grossFloorArea,
      siteArea: projectData?.siteArea,
      storeysAboveGrade: projectData?.storeysAboveGrade,
      storeysBelowGrade: projectData?.storeysBelowGrade,
      roughConstructionBudget: projectData?.roughConstructionBudget,

      startDate: toAwsDateTime(projectData?.startDate),
      estimatedCompletion: toAwsDateTime(projectData?.estimatedCompletion),

      occupancyRegion: projectData?.occupancyRegion,
      occupancyTypes: Array.isArray(projectData?.occupancyTypes) ? projectData.occupancyTypes : undefined,
      occupancyCodes: Array.isArray(projectData?.occupancyCodes) ? projectData.occupancyCodes : undefined,
      majorOccupancy: projectData?.majorOccupancy,
      constructionType: projectData?.constructionType,
      buildingHeight: projectData?.buildingHeight,
      highBuilding: projectData?.highBuilding,
      sprinklerSystem: projectData?.sprinklerSystem,
      fireAlarm: projectData?.fireAlarm,
      standpipe: projectData?.standpipe,
      hazardousSubstances: projectData?.hazardousSubstances,
      hasBuildingCodeExtractions: projectData?.hasBuildingCodeExtractions,
      projectCategory,
      entitiesData,

      status: projectData?.status,
      lastModified: new Date().toISOString(),
    };

    const input = sanitizeInput(inputRaw);

    if (process.env.NODE_ENV === 'development') {
      console.log('[UPDATE input]', { id: input.id, name: input.name, projectCategory: input.projectCategory });
    }

    try {
      const result = await graphqlRequest({
        query: updateProjectSettingsBundle,
        variables: { input },
      });
      return result.data.updateProject;
    } catch (error) {
      throw error;
    }
  }

  /** @deprecated Use updateProject with native fields. Kept for callers not yet updated. */
  async persistEntityPayload(projectId, { userDescription, projectCategory, entitiesData }) {
    return this.updateProject(projectId, {
      description: userDescription,
      projectCategory,
      entitiesData,
    });
  }

  async deleteProject(projectId) {
    try {
      // 1) delete all memberships for the project
      let nextToken = null;
      const members = [];

      do {
        const res = await graphqlRequest({
          query: projectMembersByProjectLite,
          variables: { projectId, limit: 200, nextToken },
        });
        const conn = res?.data?.projectMembersByProject;
        members.push(...(conn?.items || []));
        nextToken = conn?.nextToken || null;
      } while (nextToken);

      await Promise.all(
        members
          .filter(m => m?.id)
          .map(m =>
            graphqlRequest({
              query: deleteProjectMember,
              variables: { input: { id: m.id } },
            })
          )
      );

      // 2) delete the project
      const result = await graphqlRequest({
        query: deleteProject,
        variables: { input: { id: projectId } },
      });

      return result.data.deleteProject;
    } catch (e) {
      console.error('Error deleting project:', e);
      throw new Error('Failed to delete project: ' + (e.message || 'Unknown error'));
    }
  }
  /**
   * Re-filter a project's COMPANY-scope building-code requirements against the
   * current project settings. Cheap: reuses the already-extracted
   * CompanyRequirements (no OCR / LLM re-extraction) — the backend just re-runs
   * the project filter and rewrites the project register.
   *
   * Hits the classifierConfirm endpoint with action:'refilter' (same endpoint as
   * confirm, so no new API route/IAM). Returns the trigger response; the actual
   * work happens async and surfaces a "Requirements ready" notification.
   */
  async reFilterProject(projectId, { companyId = '', projectContext = {}, userId = '' } = {}) {
    const idToken = localStorage.getItem('id_token');
    if (!idToken) {
      const error = new Error('User is not authenticated');
      error.name = 'NoValidAuthTokens';
      throw error;
    }
    if (!projectId) throw new Error('reFilterProject: projectId is required');

    const res = await fetch(`${API_BASE_CLASSIFIER}/classifier/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        action: 'refilter',
        projectId,
        companyId,
        projectContext,
        userId,
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Re-filter request failed (${res.status}) ${text}`);
    }
    return res.json().catch(() => ({}));
  }

  /**
   * Resume a requirements extraction that crashed mid-run (e.g. company/project
   * merge failure) without re-classifying the document. The RequirementsIndex
   * row already has everything needed to re-fire EXTRACT_INIT; this just passes
   * through whatever identifiers the triggering notification's actionPayload
   * carried (companyId+resourceId for COMPANY scope, or projectId+requirementId+
   * linkId for PROJECT scope).
   *
   * Hits the classifierConfirm endpoint with action:'continue_extraction' (same
   * endpoint as confirm/refilter, so no new API route/IAM).
   */
  async continueExtraction(payload = {}) {
    const idToken = localStorage.getItem('id_token');
    if (!idToken) {
      const error = new Error('User is not authenticated');
      error.name = 'NoValidAuthTokens';
      throw error;
    }

    const res = await fetch(`${API_BASE_CLASSIFIER}/classifier/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        action: 'continue_extraction',
        ...payload,
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Continue extraction request failed (${res.status}) ${text}`);
    }
    return res.json().catch(() => ({}));
  }

  async deleteProjectCleanup(projectId) {
    const idToken = localStorage.getItem('id_token');
    if (!idToken || !projectId) return;

    try {
      fetch(process.env.REACT_APP_DELETE_PROJECT_CLEANUP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': idToken,
        },
        body: JSON.stringify({ projectId }),
      });
      // intentionally not awaited — fire and forget
    } catch (e) {
      console.warn('[deleteProjectCleanup] fire-and-forget failed to dispatch:', e);
    }
  }

}

export default new ProjectService();
